/**
 * useIcpSupply — V2.K.3
 *
 * Fetches ICP supply data from CoinGecko :
 *  - circulating_supply : tokens currently in circulation
 *  - total_supply       : ICP doesn't expose a meaningful total
 *                         (uncapped chain — minted via voting / node
 *                         rewards, burned for cycles). CoinGecko
 *                         usually returns circulating == total.
 *  - max_supply         : null on CoinGecko (ICP has NO hard cap).
 *
 * The Internet Computer is structurally different from the other
 * monitors we cover :
 *   - No max supply — emission is offset by ICP→cycles burns.
 *   - The interesting "locked" figure is the ~250 M ICP staked in
 *     NNS neurons (≈ 45 % of circulating per DFINITY tokenomics).
 *     CoinGecko doesn't expose this, so we surface it as a published
 *     reference figure plus a link to the official ICP Dashboard.
 *
 * Refresh : every 5 minutes.
 */

import { useQuery } from '@tanstack/react-query';

export interface IcpSupplyData {
  circulating: number;
  total: number;
  /** Published reference value of ICP staked in NNS neurons (≈ 45 %
   *  of circulating per DFINITY Tokenomics dashboard, early 2026). */
  nnsStakedReference: number;
  /** Current published annual inflation rate (pre-Mission 70). */
  annualInflationPct: number;
  /** Estimated annual mint based on the published inflation rate. */
  annualMint: number;
  /** % of circulating supply staked in NNS neurons. */
  nnsStakedPctOfCirculating: number;
  lastUpdated: string | null;
}

// Reference values, last verified against
// dashboard.internetcomputer.org/tokenomics & /circulation.
// These act as fallbacks when CoinGecko misses a field and as
// orientation values shown in the UI.
const ICP_NNS_STAKED_REFERENCE = 250_000_000;
const ICP_ANNUAL_INFLATION_PCT_REFERENCE = 3.62;

async function fetchIcpSupply(): Promise<IcpSupplyData> {
  const response = await fetch(
    'https://api.coingecko.com/api/v3/coins/internet-computer?localization=false&tickers=false&market_data=true&community_data=false&developer_data=false&sparkline=false',
  );
  if (!response.ok) {
    throw new Error(`CoinGecko returned ${response.status}`);
  }
  const json = await response.json();
  const md = json.market_data || {};
  const circulating = Number(md.circulating_supply) || 0;
  const total = Number(md.total_supply) || circulating;

  const annualMint = circulating * (ICP_ANNUAL_INFLATION_PCT_REFERENCE / 100);

  return {
    circulating,
    total,
    nnsStakedReference: ICP_NNS_STAKED_REFERENCE,
    annualInflationPct: ICP_ANNUAL_INFLATION_PCT_REFERENCE,
    annualMint,
    nnsStakedPctOfCirculating: circulating > 0
      ? (ICP_NNS_STAKED_REFERENCE / circulating) * 100
      : 0,
    lastUpdated: json.last_updated || null,
  };
}

export function useIcpSupply() {
  return useQuery({
    queryKey: ['icp', 'supply'],
    queryFn: fetchIcpSupply,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    retry: 2,
  });
}
